import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal } from "@/components/primitives";
import { ArchitectureDiagram, RiskSection } from "@/components/marketing";
import { trackEvent } from "@/lib/api";
import { ArrowRight } from "@phosphor-icons/react";

const STEPS = [
  { n: "01", title: "Choose Your License", desc: "A member selects the appropriate Quantum Scalp software license that matches their needs." },
  { n: "02", title: "Connect Your Trading Infrastructure", desc: "Connect supported payment, wallet, exchange, and execution infrastructure. We clearly explain what Quantum Scalp does and does not control." },
  { n: "03", title: "Q-Core Analyzes the Markets", desc: "The engine continuously analyzes available market data and searches for potential arbitrage opportunities across venues." },
  { n: "04", title: "Monitor Your Activity", desc: "Members view available trading and activity information through their dashboard, with appropriate risk disclosures." },
];

export default function HowItWorks() {
  useEffect(() => {
    trackEvent("page_view", { page: "how-it-works" });
  }, []);
  return (
    <div>
      <PageHeader
        eyebrow="How It Works"
        title="From license to live activity, transparently."
        sub="Four steps from selecting a license to monitoring activity — with a clear view of the non-custodial architecture behind it."
      />

      <Section grid>
        <div className="grid md:grid-cols-2 gap-5">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="qs-beam relative h-full rounded-xl border border-white/10 bg-[#0A0B10] p-7 overflow-hidden" data-testid={`step-${s.n}`}>
                <span className="absolute -top-6 -right-2 font-black text-[110px] leading-none text-white/[0.03] select-none">{s.n}</span>
                <div className="relative">
                  <span className="font-mono text-sm text-[#2DD4BF]">STEP {s.n}</span>
                  <h3 className="text-xl font-bold text-white mt-3 mb-2">{s.title}</h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section grid>
        <SectionHeading
          align="center"
          eyebrow="Non-Custodial Architecture"
          title="Your Assets. Your Control."
          sub="Third-party payment infrastructure may be used to process payments and withdrawals. Where assets must be transferred to trading venues for execution, that process is shown transparently below."
          className="mb-14"
        />
        <ArchitectureDiagram />
      </Section>

      <Section>
        <RiskSection />
      </Section>

      <Section>
        <div className="text-center">
          <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors">
            Choose Your License <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </Section>
    </div>
  );
}
